import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, Loader2, AlertCircle, ShoppingBag, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { api, apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full h-12 pl-11 pr-4 rounded-2xl bg-white border border-[#E8CFD1] outline-none focus:border-[#C58B90] focus:ring-2 focus:ring-[#F2E6E7] transition-[border-color,box-shadow]";
const btn =
  "w-full h-12 rounded-full bg-[#C58B90] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#A87277] transition-colors disabled:opacity-70";

export function FirstAccess() {
  const [step, setStep] = useState("email"); // email | setpw | info
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [info, setInfo] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const checkEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setInfo({ type: "", message: "" });
    try {
      const { data } = await api.post("/auth/first-access", { email });
      if (data.status === "ready") {
        setName(data.name || "");
        setStep("setpw");
      } else if (data.status === "has_password") {
        setInfo({ type: "ok", message: data.message });
        setStep("info");
      } else {
        setInfo({ type: "warn", message: data.message });
        setStep("info");
      }
    } catch (e) {
      setInfo({ type: "warn", message: apiError(e) });
      setStep("info");
    } finally {
      setLoading(false);
    }
  };

  const setPw = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/first-access/set", { email, password });
      login(data.token, data.user);
      toast.success(`Tudo pronto! Bem-vinda, ${data.user.name.split(" ")[0]} 💗`);
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#F2E6E7] blur-3xl opacity-70" />
      <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#E8CFD1] blur-3xl opacity-50" />
      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-[2rem] p-8 sm:p-10 border border-[#F2E6E7] bc-shadow-hover animate-float-up">
          <div className="flex flex-col items-center text-center mb-6">
            <Logo size={60} />
            <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#C58B90] bg-[#F2E6E7] px-3 py-1.5 rounded-full">
              <ShoppingBag size={13} /> Comprei na Kiwify
            </div>
            <h1 className="font-serif text-2xl mt-3">Primeiro acesso</h1>
          </div>

          {step === "email" && (
            <form onSubmit={checkEmail} className="space-y-4">
              <p className="text-sm text-[#7A7571] text-center">
                Use o <b>mesmo email</b> que você usou na compra da Kiwify. É rapidinho! 💗
              </p>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C58B90]" />
                <input
                  className={inputCls}
                  type="email"
                  placeholder="Email da compra"
                  data-testid="fa-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button className={btn} disabled={loading} data-testid="fa-check">
                {loading && <Loader2 size={17} className="animate-spin" />} Continuar
              </button>
              <Link to="/login" className="flex items-center justify-center gap-1 text-sm text-[#C58B90] font-semibold">
                <ArrowLeft size={15} /> Já tenho senha, fazer login
              </Link>
            </form>
          )}

          {step === "setpw" && (
            <form onSubmit={setPw} className="space-y-4" data-testid="fa-setpw-form">
              <div className="flex items-center gap-2 rounded-2xl bg-[#EAF3EB] px-4 py-3 text-sm text-[#5FA36A]">
                <CheckCircle2 size={17} className="shrink-0" />
                <span>Compra encontrada{name ? `, ${name.split(" ")[0]}` : ""}! Agora crie sua senha.</span>
              </div>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C58B90]" />
                <input
                  className={inputCls}
                  type="password"
                  placeholder="Crie sua senha (mín. 6 caracteres)"
                  data-testid="fa-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <button className={btn} disabled={loading} data-testid="fa-set">
                {loading && <Loader2 size={17} className="animate-spin" />} Criar senha e entrar
              </button>
            </form>
          )}

          {step === "info" && (
            <div className="space-y-5 text-center" data-testid="fa-info">
              <div
                className={`flex items-start gap-2 rounded-2xl px-4 py-3 text-sm text-left ${
                  info.type === "ok" ? "bg-[#EAF3EB] text-[#5FA36A]" : "bg-[#FBEDEE] text-[#A05055]"
                }`}
              >
                <AlertCircle size={17} className="mt-0.5 shrink-0" />
                <span>{info.message}</span>
              </div>
              {info.type === "ok" ? (
                <Link to="/login" className="inline-block w-full">
                  <span className={btn}>Ir para o login</span>
                </Link>
              ) : (
                <button onClick={() => setStep("email")} className={btn} data-testid="fa-try-again">
                  Tentar outro email
                </button>
              )}
              <Link to="/login" className="block text-sm text-[#C58B90] font-semibold">
                Voltar ao login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
